import React, { useState } from 'react';
import { Card, Table, Badge } from 'react-bootstrap';
import { format } from 'date-fns';

const safeFormat = (dateStr, fmt) => {
  try {
    if (!dateStr) return '--';
    return format(new Date(dateStr), fmt);
  } catch {
    return '--';
  }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('vi-VN').format(amount || 0) + 'đ';
};

const ORDER_STATUS = {
  COMPLETED: { label: 'Hoàn thành', variant: 'success' },
  CANCELLED: { label: 'Đã hủy', variant: 'danger' },
  PENDING: { label: 'Chờ xử lý', variant: 'warning' },
  CONFIRMED: { label: 'Xác nhận', variant: 'info' },
  PREPARING: { label: 'Đang pha', variant: 'primary' },
  READY: { label: 'Sẵn sàng', variant: 'info' },
  SERVED: { label: 'Đã phục vụ', variant: 'success' },
};

const PAYMENT_METHOD = {
  CASH: { label: 'Tiền mặt', variant: 'success' },
  BANK_TRANSFER: { label: 'Chuyển khoản', variant: 'info' },
  CARD: { label: 'Thẻ', variant: 'primary' },
  MOMO: { label: 'MoMo', variant: 'danger' },
  VNPAY: { label: 'VNPay', variant: 'primary' },
};

const ORDER_TYPE = {
  DINE_IN: 'Tại quán',
  TAKEAWAY: 'Mang về',
  DELIVERY: 'Giao hàng',
};

const OrdersTab = ({ orders = [], orderSummary = {} }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  return (
    <>
      {/* Summary Cards */}
      <div className='row g-3 mb-4'>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-primary bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-cart text-primary fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-4'>{orderSummary.total_orders || 0}</div>
                <div className='text-muted small'>Tổng đơn</div>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-success bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-money text-success fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-5'>{formatCurrency(orderSummary.total_revenue)}</div>
                <div className='text-muted small'>Doanh thu</div>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-warning bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-bill text-warning fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-5'>{formatCurrency(orderSummary.cash_orders?.amount)}</div>
                <div className='text-muted small'>Tiền mặt ({orderSummary.cash_orders?.count || 0})</div>
              </div>
            </Card.Body>
          </Card>
        </div>
        <div className='col-6 col-md-3'>
          <Card className='shadow-sm border-0 h-100'>
            <Card.Body className='d-flex align-items-center gap-3'>
              <div className='rounded bg-info bg-opacity-10 p-2 d-flex align-items-center justify-content-center' style={{ width: 44, height: 44 }}>
                <i className='icofont-credit-card text-info fs-4'></i>
              </div>
              <div>
                <div className='fw-bold fs-5'>{formatCurrency(orderSummary.bank_transfer_orders?.amount)}</div>
                <div className='text-muted small'>Chuyển khoản ({orderSummary.bank_transfer_orders?.count || 0})</div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Order Table */}
      <Card className='shadow-sm border-0'>
        <Card.Body className='p-0'>
          <Table responsive hover className='mb-0 align-middle'>
            <thead className='bg-light'>
              <tr>
                <th className='border-bottom-0 ps-4' style={{ width: 50 }}>#</th>
                <th className='border-bottom-0'>Mã đơn</th>
                <th className='border-bottom-0'>Loại</th>
                <th className='border-bottom-0'>Bàn</th>
                <th className='border-bottom-0'>Thanh toán</th>
                <th className='border-bottom-0 text-end'>Số tiền</th>
                <th className='border-bottom-0'>Thời gian</th>
                <th className='border-bottom-0'>Người tạo</th>
                <th className='border-bottom-0 pe-4'>Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, idx) => {
                const statusCfg = ORDER_STATUS[order.status] || { label: order.status, variant: 'secondary' };
                const paymentCfg = PAYMENT_METHOD[order.payment_method] || { label: order.payment_method || '--', variant: 'secondary' };
                const isExpanded = expandedRow === order.id;
                const hasItems = order.items?.length > 0;

                return (
                  <React.Fragment key={order.id}>
                    <tr
                      onClick={() => hasItems && setExpandedRow(isExpanded ? null : order.id)}
                      style={{ cursor: hasItems ? 'pointer' : 'default' }}
                    >
                      <td className='ps-4 text-muted'>
                        {idx + 1}
                        {hasItems && (
                          <i className={`icofont-simple-${isExpanded ? 'up' : 'down'} ms-1 text-muted`} style={{ fontSize: '0.7rem' }}></i>
                        )}
                      </td>
                      <td className='fw-medium'>{order.order_number}</td>
                      <td><span className='small'>{ORDER_TYPE[order.order_type] || order.order_type}</span></td>
                      <td className='text-muted small'>{order.table_number ? `Bàn ${order.table_number}` : '—'}</td>
                      <td><Badge bg={paymentCfg.variant}>{paymentCfg.label}</Badge></td>
                      <td className='text-end fw-medium'>{formatCurrency(order.total_amount)}</td>
                      <td className='text-muted small'>{safeFormat(order.paid_at || order.created_at, 'HH:mm')}</td>
                      <td className='text-muted small'>{order.created_by?.full_name || '—'}</td>
                      <td className='pe-4'><Badge bg={statusCfg.variant}>{statusCfg.label}</Badge></td>
                    </tr>

                    {/* Expanded Items Row */}
                    {isExpanded && hasItems && (
                      <tr>
                        <td colSpan='9' className='bg-light px-4 py-2'>
                          <div className='small fw-medium mb-2'>Chi tiết đơn hàng ({order.items.length} món)</div>
                          <Table size='sm' className='mb-0' borderless>
                            <thead>
                              <tr className='text-muted'>
                                <th className='fw-normal'>Tên món</th>
                                <th className='fw-normal text-center' style={{ width: 60 }}>SL</th>
                                <th className='fw-normal text-end' style={{ width: 100 }}>Đơn giá</th>
                                <th className='fw-normal text-end' style={{ width: 100 }}>Thành tiền</th>
                                <th className='fw-normal' style={{ width: 150 }}>Ghi chú</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.items.map((item) => (
                                <tr key={item.id}>
                                  <td className='small'>
                                    <div>{item.name || '—'}</div>
                                    {(item.customer_phone || item.customerPhone) && (
                                      <div className='text-muted' style={{ fontSize: '0.75rem' }}>
                                        SĐT món: {item.customer_phone || item.customerPhone}
                                      </div>
                                    )}
                                  </td>
                                  <td className='small text-center'>{item.quantity}</td>
                                  <td className='small text-end'>{formatCurrency(item.unit_price)}</td>
                                  <td className='small text-end fw-medium'>{formatCurrency(item.subtotal)}</td>
                                  <td className='small text-muted'>{item.notes || ''}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          {order.discount_amount > 0 && (
                            <div className='text-end small text-muted mt-1'>
                              Giảm giá: -{formatCurrency(order.discount_amount)}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {orders.length === 0 && (
                <tr>
                  <td colSpan='9' className='text-center py-4 text-muted'>
                    Không có đơn hàng nào trong khoảng thời gian ca
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </>
  );
};

export default OrdersTab;
